import { similarHref } from '@/features/artists/artistHref'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import { useAddTrack, useJobs, useToggleFavorite } from '@/api/hooks'
import type { LibraryItem } from '@/api/types'
import { toQueueItem, usePlayer, type QueueItem } from '@/audio/store'
import type { Selection } from '@/components/selection'
import { toast } from '@/components/ui'

import { AddToPlaylistDialog } from './AddToPlaylistDialog'
import { errorMessage } from './errorMessage'
import { LibraryTrackRow } from './LibraryTrackRow'
import { RemoveTrackDialog } from './RemoveTrackDialog'
import { TrackDetailsDialog } from './TrackDetailsDialog'

export interface LibraryTrackListProps {
  /** The rows to render, already in the order they should play. */
  items: LibraryItem[]
  selection: Selection<number>
  /** Selection mode, including the state where nothing is ticked yet. */
  selecting: boolean
}

/**
 * A list of library items with everything a row can do: play, favourite, queue,
 * playlist, remove, retry, and multi-select.
 *
 * Shared by the Songs and Favourites lists and by an album's track list, so all
 * three behave the same way — pressing a row plays the list it is in, not the
 * track on its own.
 */
export function LibraryTrackList({ items, selection, selecting }: LibraryTrackListProps) {
  // The shell owns the SSE connection; this only reads the cache it keeps warm.
  const jobs = useJobs('active')

  /** Live download progress, keyed by library item. */
  const progressById = useMemo(() => {
    const map = new Map<number, number>()
    for (const job of jobs.data?.results ?? []) {
      if (job.library_item_id !== null && (job.status === 'queued' || job.status === 'running')) {
        map.set(job.library_item_id, job.progress)
      }
    }
    return map
  }, [jobs.data])

  // ---------------------------------------------------------------------
  // Playback
  // ---------------------------------------------------------------------

  const playQueue = usePlayer((state) => state.playQueue)
  const enqueue = usePlayer((state) => state.enqueue)
  const playNextUp = usePlayer((state) => state.playNextUp)
  const isPlaying = usePlayer((state) => state.isPlaying)
  const activeId = usePlayer((state) => state.current()?.libraryItemId ?? null)

  /** Everything on screen that can actually be played, in display order. */
  const playable = useMemo(
    () =>
      items
        .map(toQueueItem)
        .filter((entry): entry is QueueItem => entry !== null),
    [items],
  )

  const explainUnplayable = useCallback((item: LibraryItem) => {
    if (item.status === 'failed' || item.status === 'missing') {
      toast.error('This song is not available', 'Download it again to play it.')
      return
    }
    toast.info('Still downloading', 'It will be ready to play once the download finishes.')
  }, [])

  const handlePlay = useCallback(
    (item: LibraryItem) => {
      const start = playable.findIndex((entry) => entry.libraryItemId === item.id)
      if (start === -1) {
        explainUnplayable(item)
        return
      }
      // Pressing a row plays the list, not the track — that is what makes this
      // feel like a music app rather than a file browser.
      playQueue(playable, start, { source: 'library' })
    },
    [playable, playQueue, explainUnplayable],
  )

  const handlePlayNext = useCallback(
    (item: LibraryItem) => {
      const entry = toQueueItem(item)
      if (!entry) {
        explainUnplayable(item)
        return
      }
      playNextUp([entry])
      toast.info('Playing next', item.track.title)
    },
    [playNextUp, explainUnplayable],
  )

  const handleAddToQueue = useCallback(
    (item: LibraryItem) => {
      const entry = toQueueItem(item)
      if (!entry) {
        explainUnplayable(item)
        return
      }
      enqueue([entry])
      toast.info('Added to queue', item.track.title)
    },
    [enqueue, explainUnplayable],
  )

  // ---------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------

  const toggleFavorite = useToggleFavorite()
  const navigate = useNavigate()
  const addTrack = useAddTrack()
  const [favoriteOverrides, setFavoriteOverrides] = useState<Record<number, boolean>>({})
  const [retryingId, setRetryingId] = useState<number | null>(null)
  const [detailsTarget, setDetailsTarget] = useState<LibraryItem | null>(null)
  const [removeTarget, setRemoveTarget] = useState<LibraryItem | null>(null)
  const [playlistTarget, setPlaylistTarget] = useState<LibraryItem | null>(null)

  // The heart flips immediately and the refetch confirms it a moment later.
  // Once the server agrees — or the row is gone — the override is dropped so the
  // two can never disagree indefinitely.
  useEffect(() => {
    setFavoriteOverrides((previous) => {
      const entries = Object.entries(previous)
      if (entries.length === 0) return previous
      const server = new Map(items.map((item) => [item.id, item.is_favorite]))
      const kept: Record<number, boolean> = {}
      for (const [id, value] of entries) {
        const known = server.get(Number(id))
        if (known !== undefined && known !== value) kept[Number(id)] = value
      }
      return Object.keys(kept).length === entries.length ? previous : kept
    })
  }, [items])

  const handleToggleFavorite = useCallback(
    (item: LibraryItem, currentValue: boolean) => {
      const next = !currentValue
      setFavoriteOverrides((previous) => ({ ...previous, [item.id]: next }))
      toggleFavorite.mutate(
        { id: item.id, favorite: next },
        {
          onError: (cause) => {
            setFavoriteOverrides((previous) => ({ ...previous, [item.id]: currentValue }))
            toast.error('Could not update favourites', errorMessage(cause))
          },
        },
      )
    },
    [toggleFavorite],
  )

  // A failed or missing item has no job to resume, so re-adding the track is the
  // retry: the server reuses the existing LibraryItem and dispatches a new job.
  const handleRetry = useCallback(
    (item: LibraryItem) => {
      setRetryingId(item.id)
      addTrack.mutate(
        { provider: item.track.provider, external_id: item.track.external_id },
        {
          onSuccess: () => toast.success('Download restarted', item.track.title),
          onError: (cause) => toast.error('Could not start the download', errorMessage(cause)),
          onSettled: () => setRetryingId(null),
        },
      )
    },
    [addTrack],
  )

  // ---------------------------------------------------------------------
  // Selection
  // ---------------------------------------------------------------------

  const orderedIds = useMemo(() => items.map((item) => item.id), [items])

  const handleToggleSelected = useCallback(
    (id: number, shiftKey: boolean) => {
      if (shiftKey) selection.toggleRange(id, orderedIds)
      else selection.toggle(id)
    },
    [selection, orderedIds],
  )

  return (
    <>
      {/* No gap between rows: the skeleton is a stack of bare h-16 rows, so
          the real list has to drop in at exactly the same rhythm. */}
      <ul>
        {items.map((item) => {
          const favorite = favoriteOverrides[item.id] ?? item.is_favorite
          return (
            <li key={item.id}>
              <LibraryTrackRow
                item={item}
                isActive={activeId === item.id}
                isPlaying={isPlaying}
                isFavorite={favorite}
                progress={progressById.get(item.id)}
                isRetrying={retryingId === item.id}
                onPlay={() => handlePlay(item)}
                onToggleFavorite={() => handleToggleFavorite(item, favorite)}
                onPlayNext={() => handlePlayNext(item)}
                onAddToQueue={() => handleAddToQueue(item)}
                onAddToPlaylist={() => setPlaylistTarget(item)}
                onMoreLikeThis={
                  item.track.artist_id
                    ? () => void navigate(similarHref(item.track))
                    : undefined
                }
                onDetails={() => setDetailsTarget(item)}
                onRemove={() => setRemoveTarget(item)}
                onRetry={() => handleRetry(item)}
                selectionActive={selecting}
                isSelected={selection.isSelected(item.id)}
                onToggleSelected={(shiftKey) => handleToggleSelected(item.id, shiftKey)}
                onLongPress={selecting ? undefined : () => selection.select(item.id)}
              />
            </li>
          )
        })}
      </ul>

      {detailsTarget ? <TrackDetailsDialog item={detailsTarget} onClose={() => setDetailsTarget(null)} /> : null}

      {removeTarget ? (
        <RemoveTrackDialog item={removeTarget} onClose={() => setRemoveTarget(null)} />
      ) : null}

      {playlistTarget ? (
        <AddToPlaylistDialog item={playlistTarget} onClose={() => setPlaylistTarget(null)} />
      ) : null}
    </>
  )
}
